import { useState } from 'react'
import Trans from 'next-translate/Trans'
import { GrPause, GrPlay } from 'react-icons/gr'
import { Link } from '@app/components/stateless/typo/link'
import { Header3, Header4 } from '@app/components/general/header'
import Image from 'next/image'
import { accessibilityCoverage } from '@app/lib/static/coverage'
import { classNames } from '@app/utils/classes'
import { useTranslation } from '@app/lib/hooks/useTranslation'
import { Paragraph } from '../typo/paragraph'

export const MarketingCli = () => {
  const { t } = useTranslation('cli-features')
  const [sampleVisible, setSample] = useState<boolean>(false)

  const toggleSample = () => setSample((x) => !x)

  const videoText = t(sampleVisible ? 'pause-video' : 'watch-video')

  return (
    <div className='pt-32'>
      <div className='md:ring-1 md:ring-[#0E1116] md:ring-offset-8 md:ring-offset-gray-600 py-3 md:px-5 rounded'>
        <div className='flex space-x-3 place-items-center'>
          <Header3>{t('header')}</Header3>
          <button
            title={videoText}
            className='px-3 rounded-3xl border h-10 w-10 hover:opacity-70'
            onClick={toggleSample}
            type={'button'}
          >
            <>
              <span className='sr-only'>{videoText}</span>
              {sampleVisible ? (
                <GrPause className='grIcon' height={20} width={20} />
              ) : (
                <GrPlay className='grIcon' height={20} width={20} />
              )}
            </>
          </button>
        </div>

        <Trans
          i18nKey='cli-features:header-details'
          components={[
            <p className='text-lg pb-4 leading-10' key={'cli-1'} />,
            <code className='text-gray-700  bg-gray-300 p-1' key={'cli-2'} />,
          ]}
        />

        <div className={`flex flex-wrap gap-x-5 space-y-4 place-items-center`}>
          <div className='flex flex-1 sm:w-1/3 py-5 place-items-center'>
            <div className={sampleVisible ? 'block' : 'hidden'}>
              <Image
                src={'/img/a11ywatch_cli.gif'}
                height={494}
                width={717}
                alt={t('video-alt')}
                className={'h-auto w-auto rounded'}
                quality={70}
                style={{ color: undefined }}
              />
            </div>
            <div
              className={classNames(
                sampleVisible ? 'hidden' : 'block',
                `h-full flex place-items-center md:h-initial flex-1 w-full`
              )}
            >
              <div
                className={`bg-[#0E1116] rounded shadow-xl w-full border dark:border-gray-800`}
              >
                <div className='py-2 px-1 text-sm md:text-base lg:text-xl md:text-lg md:py-3 md:px-3'>
                  <div className='text-gray-100'>
                    <span className='text-gray-300 select-none'>~ </span> npm i
                    a11ywatch-cli -g
                  </div>
                  <div className='text-gray-200'>
                    <span className='text-gray-300 select-none'>~ </span>{' '}
                    a11ywatch -s $TOKEN
                  </div>
                  <div className='text-gray-300'>
                    <span className='text-gray-400 select-none'>~ </span>{' '}
                    a11ywatch crawl -e -d -u a11ywatch.com
                    <span
                      className={
                        'w-[2.5px] h-[1.25rem] md:w-[8px] inline-block bg-gray-200 dark:bg-white ml-[0.18rem] translate-y-1'
                      }
                    ></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='md:w-1/2 sm:w-1/3'>
            <ul className={'text-left space-y-3'}>
              {[0, 1, 2].map((detail: number, i: number) => (
                <li key={i}>
                  <Header4>{t(`features.${detail}.header`)}</Header4>
                  <Paragraph>
                    {t(
                      `features.${detail}.details`,
                      detail === 0
                        ? {
                            coverage: accessibilityCoverage,
                          }
                        : undefined
                    )}
                  </Paragraph>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className='flex py-6 place-content-center text-center'>
        <Link
          href={'https://docs.a11ywatch.com/documentation/cli/'}
          target='_blank'
          rel='referrer'
          className={classNames(
            'border dark:border-gray-600 px-4 py-2 rounded bg-[#0E1116] dark:bg-white dark:text-black',
            'text-white no-underline hover:no-underline text-lg hover:bg-blue-600 dark:hover:bg-blue-700 dark:hover:text-white hover:border-blue-600 dark:hover:border-blue-600 hover:decoration-blue-600'
          )}
        >
          {t('view-details')}
        </Link>
      </div>
    </div>
  )
}
